import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';

export type VbDialogSize = 'small' | 'medium' | 'large' | 'xlarge' | 'fullscreen';

@Component({
  selector: 'vb-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule],
  templateUrl: './vb-dialog.component.html',
  styleUrls: ['./vb-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated
})
export class VbDialogComponent {
  @Input() visible = false;
  @Input() header = '';
  @Input() subHeader = '';
  @Input() size: VbDialogSize = 'medium';
  @Input() modal = true;
  @Input() closable = true;
  @Input() closeOnEscape = true;
  @Input() dismissableMask = false;
  @Input() draggable = false;
  @Input() resizable = false;
  @Input() maximizable = false;
  @Input() appendTo: string | HTMLElement = 'body';
  @Input() styleClass = '';
  @Input() blockScroll = true;
  @Input() showHeader = true;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() show = new EventEmitter<void>();
  @Output() hide = new EventEmitter<void>();
  @Output() maximize = new EventEmitter<{ maximized: boolean }>();

  @ContentChild('headerTemplate') headerTemplate?: TemplateRef<unknown>;
  @ContentChild('footerTemplate') footerTemplate?: TemplateRef<unknown>;

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
  }

  onDialogShow(): void {
    this.show.emit();
  }

  onDialogHide(): void {
    this.hide.emit();
  }

  onDialogMaximize(event: { maximized: boolean }): void {
    this.maximize.emit(event);
  }

  close(): void {
    this.onVisibleChange(false);
  }

  get dialogStyle(): Record<string, string> {
    switch (this.size) {
      case 'small':
        return { width: '30vw', 'max-width': '400px' };
      case 'medium':
        return { width: '50vw', 'max-width': '700px' };
      case 'large':
        return { width: '70vw', 'max-width': '1000px' };
      case 'xlarge':
        return { width: '85vw', 'max-width': '1400px' };
      case 'fullscreen':
        return { width: '95vw', height: '95vh' };
      default:
        return { width: '50vw' };
    }
  }

  get dialogStyleClass(): string {
    const classes = ['vb-dialog'];
    classes.push(`vb-dialog--${this.size}`);
    if (this.styleClass) classes.push(this.styleClass);
    return classes.join(' ');
  }
}
